import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
import {Figure} from '~/components/Figure';
import {Quiz} from '~/components/Quiz';
export const meta = {
  title: 'DIP | Введение',
  description: 'Введение в принцип инверсии зависимостей, его смысл, основные понятия'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h1: "h1",
    h2: "h2",
    li: "li",
    p: "p",
    section: "section",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_components.section, {
    children: [_jsx(_components.h1, {
      children: "Введение"
    }), _jsx(_components.p, {
      children: "Программные системы состоят из модулей, которые мы можем условно поделить на низкоуровневые и высокоуровневые."
    }), _jsxs(_components.p, {
      children: [_jsx(_components.em, {
        children: "Низкоуровневые"
      }), " содержат утилитарную функциональность: обращение к базе данных, запросы к серверу, рендеринг DOM-элементов на странице."]
    }), _jsxs(_components.p, {
      children: [_jsx(_components.em, {
        children: "Высокоуровневые"
      }), " содержат сложную, более абстрактную бизнес-логику. Они достаточно абстрактны, чтобы их можно было переиспользовать в разных проектах: авторизация пользователей, валидация форм, отправка уведомлений."]
    }), _jsx(_components.p, {
      children: "В устойчивых системах высокоуровневые модули, как правило, не требуют обновления при изменении низкоуровневых. Добиться подобной устойчивости помогает принцип инверсии зависимостей."
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Принцип инверсии зависимостей"
      }), _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%B8%D0%BD%D0%B2%D0%B5%D1%80%D1%81%D0%B8%D0%B8_%D0%B7%D0%B0%D0%B2%D0%B8%D1%81%D0%B8%D0%BC%D0%BE%D1%81%D1%82%D0%B5%D0%B9",
          children: "Принцип инверсии зависимостей"
        }), " (Dependency Inversion Principle, DIP) предполагает, что:"]
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Высокоуровневые модули не должны зависеть от низкоуровневых; оба типа должны зависеть от абстракций."
        }), "\n", _jsx(_components.li, {
          children: "Абстракции не должны зависеть от деталей, детали должны зависеть от абстракций."
        }), "\n"]
      }), _jsxs(_components.p, {
        children: ["Таким образом DIP помогает снизить ", _jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%97%D0%B0%D1%86%D0%B5%D0%BF%D0%BB%D0%B5%D0%BD%D0%B8%D0%B5_(%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5)",
          children: "зацепление модулей"
        }), " (coupling)."]
      }), _jsxs(_components.p, {
        children: ["Когда модули жёстко зацеплены, они слишком много знают друг о друге и не функционируют по отдельности. В такой ситуации изменения в одном будут требовать изменений в других — что нарушает ", _jsx(_components.a, {
          href: "/ocp",
          children: "OCP"
        }), "."]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Зацепление и связность"
      }), _jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "Зацепление"
        }), " (coupling) не стоит путать со ", _jsx(_components.em, {
          children: "связностью"
        }), " (cohesion)."]
      }), _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%97%D0%B0%D1%86%D0%B5%D0%BF%D0%BB%D0%B5%D0%BD%D0%B8%D0%B5_(%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5)",
          children: "Зацепление"
        }), " — степень взаимозависимости разных модулей. Чем выше зацепление, тем более хрупкой получается система, и тем сложнее вносить изменения."]
      }), _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://ru.wikipedia.org/wiki/%D0%A1%D0%B2%D1%8F%D0%B7%D0%BD%D0%BE%D1%81%D1%82%D1%8C_(%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5)",
          children: "Связность"
        }), " — степень, в которой задачи некоторого модуля, связаны друг с другом. Чем выше связность, тем строже модули следуют ", _jsx(_components.a, {
          href: "/srp",
          children: "SRP"
        }), ", тем выше сфокусирован модуль на конкретной задаче."]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Абстракции"
      }), _jsx(_components.p, {
        children: "Согласно принципу модули должны зависеть от других модулей не напрямую, а от абстракций."
      }), _jsxs(_components.p, {
        children: ["В ", _jsx(_components.a, {
          href: "http://stg-tud.github.io/sedc/Lecture/ws13-14/3.5-DIP.html#mode=document",
          children: "примере c регулятором температуры"
        }), " на схеме ниже структура системы нарушает DIP. Модули зависят напрямую от других модулей, это увеличивает зацепление."]
      }), _jsxs(Figure, {
        children: [_jsx("img", {
          src: "/dip-violation.svg"
        }), _jsx("figcaption", {
          children: "Модули зависят от конкретных модулей, это увеличивает зацепление"
        })]
      }), _jsx(_components.p, {
        children: "Исправленный вариант вводит прослойку между сущностями в виде абстракций — интерфейсов."
      }), _jsx(_components.p, {
        children: "Модулям теперь не обязательно работать с конкретными модулями, они могут работать с любой сущностью, которая реализует указанный интерфейс. Так снижается зацепление."
      }), _jsxs(Figure, {
        children: [_jsx("img", {
          src: "/dip-compliance.svg"
        }), _jsx("figcaption", {
          children: "Абстракции в виде интерфейсов позволяют снизить зацепление"
        })]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "DIP и тестируемость"
      }), _jsx(_components.p, {
        children: "При тестировании модуля, который зависит от других модулей, нам нужно либо создавать экземпляр каждой зависимости, либо создать заглушки."
      }), _jsx(_components.p, {
        children: "DIP упрощает тестирование системы. Если модули зависят от интерфейсов, нам достаточно создать заглушку, реализующую этот интерфейс."
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Коротко"
      }), _jsx(_components.p, {
        children: "Принцип инверсии зависимостей:"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "вводит правила и ограничения для зависимости одних модулей от других;"
        }), "\n", _jsx(_components.li, {
          children: "снижает зацепление модулей;"
        }), "\n", _jsx(_components.li, {
          children: "делает тестирование модулей проще;"
        }), "\n", _jsx(_components.li, {
          children: "позволяет проектировать систему так, чтобы модули были заменяемы на другие."
        }), "\n"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Материалы к разделу"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%9F%D1%80%D0%B8%D0%BD%D1%86%D0%B8%D0%BF_%D0%B8%D0%BD%D0%B2%D0%B5%D1%80%D1%81%D0%B8%D0%B8_%D0%B7%D0%B0%D0%B2%D0%B8%D1%81%D0%B8%D0%BC%D0%BE%D1%81%D1%82%D0%B5%D0%B9",
            children: "Принцип инверсии зависимостей, Википедия"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://blog.byndyu.ru/2009/12/blog-post.html",
            children: "Принцип инверсии зависимостей, Александр Бындю"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://makedev.org/principles/solid/dip.html",
            children: "Принцип инверсии зависимостей, MakeDev"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%97%D0%B0%D1%86%D0%B5%D0%BF%D0%BB%D0%B5%D0%BD%D0%B8%D0%B5_(%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5)",
            children: "Зацепление модулей"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%A1%D0%B2%D1%8F%D0%B7%D0%BD%D0%BE%D1%81%D1%82%D1%8C_(%D0%BF%D1%80%D0%BE%D0%B3%D1%80%D0%B0%D0%BC%D0%BC%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5)",
            children: "Связность модулей"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/GRASP",
            children: "General Responsibility Assignment Software Patterns, GRASP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://martinfowler.com/articles/injection.html",
            children: "Inversion of Control Containers and the Dependency Injection pattern"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "http://stg-tud.github.io/sedc/Lecture/ws13-14/3.5-DIP.html#mode=document",
            children: "Пример с терморегулятором"
          })
        }), "\n"]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Вопросы"
      }), _jsx(Quiz, {
        name: "dip-intro-1"
      }), _jsx(Quiz, {
        name: "dip-intro-2"
      }), _jsx(Quiz, {
        name: "dip-intro-3"
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  return _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  });
}
